import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MessageSquare, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General", message: "" });
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill in your name, email and message");
    try { await api.post("/contact", form); setSent(true); toast.success("Message sent! We'll be in touch shortly."); }
    catch { toast.error("Could not send. Please try again."); }
  };

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
      <span className="overline">Contact</span>
      <h1 className="mt-2 text-4xl lg:text-5xl">Let's talk</h1>
      <p className="mt-3 max-w-xl text-taupe">Whether you're planning a stay or exploring management, our team responds fast — by phone, email, live chat, or SMS.</p>

      <div className="mt-10 grid gap-8 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-1">
          {[
            { icon: Phone, label: "Phone", value: "(210) 274-3556" },
            { icon: Mail, label: "Email", value: "EliteSTRhostingco@yahoo.com" },
            { icon: MessageSquare, label: "SMS / Live Chat", value: "Text or use the concierge widget" },
            { icon: MapPin, label: "Service Area", value: "San Antonio · Hill Country · New Braunfels" },
          ].map((c, i) => (
            <div key={i} className="flex items-start gap-3 rounded-2xl border border-black/5 bg-white p-5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream"><c.icon size={18} strokeWidth={1.5} /></span>
              <div><div className="overline">{c.label}</div><div className="mt-1 text-ink">{c.value}</div></div>
            </div>
          ))}
          <div className="overflow-hidden rounded-2xl border border-black/5">
            <iframe title="office map" className="h-48 w-full" src="https://www.openstreetmap.org/export/embed.html?bbox=-98.90%2C30.25%2C-98.84%2C30.30&layer=mapnik&marker=30.274%2C-98.871" />
          </div>
        </div>

        <div className="lg:col-span-2">
          {sent ? (
            <div data-testid="contact-success" className="flex h-full min-h-[300px] flex-col items-center justify-center rounded-3xl card-lux p-10 text-center">
              <p className="text-2xl">Thank you ✦</p>
              <p className="mt-2 text-taupe">Your message is in. A team member will reach out shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl card-lux p-7">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input data-testid="contact-name" placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-2xl border-black/10" />
                <Input data-testid="contact-email" type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-2xl border-black/10" />
                <Input data-testid="contact-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-2xl border-black/10" />
                <select data-testid="contact-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="rounded-2xl border border-black/10 bg-white px-4 text-sm">
                  {["General", "Booking Help", "Property Management", "Press"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <Textarea data-testid="contact-message" placeholder="How can we help?" value={form.message} onChange={(e) => set("message", e.target.value)} className="mt-4 min-h-[140px] rounded-2xl border-black/10" />
              <Button data-testid="contact-submit" type="submit" className="mt-4 h-12 w-full rounded-full btn-ink">Send message</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
